import React, { memo } from 'react';
import { Position, NodeProps } from '@xyflow/react';
// @ts-ignore
import { stratify } from 'd3-hierarchy';
import { Column } from '../../../../utils/tableService';
import BaseNode from '../BaseNode';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isOutOfSync } from '../../utils/definitionLink';
import type { RootState as AppState } from '../../../../store';
import { useConnectionGone } from '../useConnectionGone';

interface DataNodeData {
  label: string;
  config: {
    // CSV Data 속성
    filepath?: string;
    delimiter?: string;
    hasHeader?: boolean;
    encoding?: string;
    mode?: string;
    
    // RDB Data 속성 — 커넥션(접속 계정) 안에서 스키마를 골라 그 스키마의 테이블을 쓴다
    connection?: string;
    schema?: string;
    table?: string;
    writeMode?: string;
    columns?: string[];
    tableColumns?: Column[];
    dataDefinitionId?: string;
  };
}

interface RootState {
  workflow: {
    hoveredNodeId: string | null;
  };
}

const DataNode: React.FC<NodeProps<any>> = ({ data, selected, type, id, ...props }) => {
  const { t } = useTranslation();
  const nodeData = data as DataNodeData;
  const hoveredNodeId = useSelector((state: RootState) => state.workflow.hoveredNodeId);
  const definitions = useSelector((state: AppState) => state.dataDefinitions.items);
  const isHovered = hoveredNodeId === id;
  
  console.log('DataNode 렌더링:', id, 'isHovered:', isHovered, 'hoveredNodeId:', hoveredNodeId);
  console.log('props에서 전달된 isHovered:', (props as any).isHovered);
  
  // 훅이라 아래의 이른 return 보다 앞에서 부른다
  const connGone = useConnectionGone(type === 'rdbData' ? nodeData?.config?.connection : undefined);

  if (!nodeData) {
    return null;
  }

  // 노드 타입에 따라 아이콘 결정
  const isCSV = type === 'csvData';

  // 필수 항목이 비었으면 '!' — SAM파일은 파일 경로, DB테이블은 연결과 테이블.
  // 스키마는 비워도 된다(연결의 기본 스키마를 쓴다 — 샘플의 DB테이블 노드 32개가 모두 그렇게 돈다).
  // 예전에는 '데이터 정의를 골랐거나 커넥션+테이블' 이면 정상으로 봤는데, 속성 폼이 연결을 고르면 첫 테이블을
  // 멋대로 채워서 테이블을 고르지 않은 노드의 '!' 가 사라졌다(현장 2026-09-17). 이제 무엇이 빠졌는지도 말한다.
  const cfg = nodeData.config || {};
  // 내부데이터는 상류 결과를 담는 노드라 소스 지정 대상이 아니다
  const missing: string[] = type === 'innerData' ? []
    : isCSV ? (cfg.filepath ? [] : [t('workflow:data.filePath')])
      : [!cfg.connection && t('workflow:data.connection'), !cfg.table && t('workflow:data.table')].filter(Boolean) as string[];
  // 데이터 정의와의 연결 — 정의가 없어졌거나(삭제), 정의와 값이 다르면(연결 전에 만든 노드) 알린다.
  // 정의 목록을 아직 받지 못했으면(비어 있으면) 판단하지 않는다.
  const def = cfg.dataDefinitionId ? definitions.find((d) => d.id === cfg.dataDefinitionId) : undefined;
  const linkWarning = type === 'innerData' || !cfg.dataDefinitionId || definitions.length === 0 ? undefined
    : !def ? t('workflow:data.defGone')
      : isOutOfSync({ type, data: { config: cfg } }, def) ? t('workflow:data.defOutOfSync', { name: def.name }) : undefined;
  const warning = missing.length ? t('workflow:data.missing', { fields: missing.join(', ') }) : connGone || linkWarning;
  
  const icon = isCSV ? (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
      <line x1="16" y1="13" x2="8" y2="13"></line>
      <line x1="16" y1="17" x2="8" y2="17"></line>
    </svg>
  ) : (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path>
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>
    </svg>
  );

  // 노드 아래에 한 줄로 붙는 설명 — 파일 경로나 스키마.테이블. 읽기/쓰기 같은 말은 붙이지 않는다
  // (좁은 노드에서 자리만 차지하고, 방향은 화살표로 이미 보인다)
  let subtitle = '';
  if (isCSV) {
    subtitle = nodeData.config?.filepath || '';
  } else if (nodeData.config?.table) {
    subtitle = `${nodeData.config.schema ? `${nodeData.config.schema}.` : ''}${nodeData.config.table}`;
  }

  return (
    <BaseNode
      id={id}
      data={data}
      selected={selected}
      type={type}
      isHovered={isHovered}
      icon={icon}
      label={nodeData.label || t('workflow:node.unnamed')}
      subtitle={subtitle}
      warning={warning}
      {...props}
    />
  );
};

export default memo(DataNode); 